/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get */
import LineItem from '@linc-technologies/linced-common/models/finance/line-item';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import { attr, belongsTo, type AsyncBelongsTo } from '@warp-drive/legacy/model';
import moment from 'moment';
import type CostCentre from 'linced-app/models/finance/cost-centre';
import type InternalOrder from 'linced-app/models/finance/internal-order';
import type Ledger from 'linced-app/models/finance/ledger';
import type LedgerItem from 'linced-app/models/finance/ledger-item';

// Proto: finance.LineItem.
export default class _LineItem extends LineItem {
  declare [Type]: 'finance/line-item';

  @belongsTo('finance/ledger', { async: true, inverse: null })
  declare ledgerId: AsyncBelongsTo<Ledger>;

  @alias('ledgerId')
  declare ledger: AsyncBelongsTo<Ledger>;

  @belongsTo('finance/ledger-item', { async: true, inverse: null })
  declare ledgerItemId: AsyncBelongsTo<LedgerItem>;

  @alias('ledgerItemId')
  declare ledgerItem: AsyncBelongsTo<LedgerItem>;

  @computed('date', 'name', 'balance', 'account.person.firstName')
  get summary() {
    const balance = '$' + this.balance.toFixed(2);
    const thisYear = moment().format('YYYY');
    const year = moment(this.date).format('YYYY');
    let yearPrefix = '';
    if (year !== thisYear) {
      yearPrefix = `[${year}] `;
    }
    return `${yearPrefix}${this.name} (${
      this.get('account.person.firstName') as string
    } ${balance} owing)`;
  }

  // Extra properties for NSW.
  // isRefundable flags whether Line Items based off this Ledger Item are
  // able to be refunded.
  @attr('boolean', { defaultValue: false })
  declare isRefundable: boolean;

  // costCentreId is the unique ID of the linked Cost Centre, if any.
  @belongsTo('finance/cost-centre', { async: true, inverse: null })
  declare costCentreId: AsyncBelongsTo<CostCentre>;

  @alias('costCentreId')
  declare costCentre: AsyncBelongsTo<CostCentre>;

  // internalOrderId is the unique ID of the linked Internal Order, if any.
  @belongsTo('finance/internal-order', { async: true, inverse: null })
  declare internalOrderId: AsyncBelongsTo<InternalOrder>;

  @alias('internalOrderId')
  declare internalOrder: AsyncBelongsTo<InternalOrder>;

  // nswSapStatus is the SAP sync status for this Line Item, if applicable.
  @attr('number', { defaultValue: 0 })
  declare nswSapStatus: number;

  // nswSapMessage is the SAP sync message for this Line Item, if applicable.
  @attr('string', { defaultValue: '' })
  declare nswSapMessage: string;
}
